import { type TriggerProps } from '@atlaskit/popup';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  useAnalytics,
} from '@databases/analytics';
import {
  useCommentActionsContext,
  useCommentStateContext,
} from '@databases/comments/hooks/useCommentContext';
import SelectValueMenu from '@databases/context-menu/select-value-menu';
import { createContextualMenuContext } from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-context';
import {
  createContextualMenuItems,
  type MenuItemMap,
} from '@databases/context-menu/select-value-menu/utils/create-contextual-menu-items';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { useDialog } from '@databases/dialog';
import { cellContextMenuMetric } from '@databases/frontend-metrics/metrics/cell-context-menu';
import { cellSelectionMetric } from '@databases/frontend-metrics/metrics/cell-selection';
import { useMetric } from '@databases/frontend-metrics/use-metric';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { calculate2DValueRange } from '@databases/redux/cellSelections/utils';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import { blurActiveElement } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { getValueId } from '@databases/utils/utils';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import LegacyTableLayoutCell, {
  type LegacyTableLayoutCellProps,
} from './LegacyTableLayoutCell';

type TableLayoutCellWithMenuProps = LegacyTableLayoutCellProps;

/**
 * @deprecated
 *
 * During the `databasesSelectionsPerformance`, edits to TableLayoutCellWithMenu and createContextMenuEffect
 * must be kept in parity.
 */
const TableLayoutCellWithMenu = React.memo(function TableLayoutCellWithMenu({
  contentId,
  entryId,
  fieldId,
  rowIndex,
  colIndex,
  isContentEditable,
  canEditContent,
  canEditFields,
  isSelectionDisabled,
  currentViewId,
  ...LegacyTableLayoutCellProps
}: TableLayoutCellWithMenuProps) {
  const store = defaultStoreUtils.useStore();
  const analyticsClient = useAnalytics();
  const selectionsActions = getService('SelectionsActions');
  const { setOpenThread } = useCommentActionsContext();
  const { openThread } = useCommentStateContext();
  const { formatMessage } = useIntl();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { toggleOpenMenu, closeMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
  });

  const { fire: fireCellSelectionMetric } = useMetric(cellSelectionMetric);
  const { fire: fireCellContextMenuMetric } = useMetric(cellContextMenuMetric);

  /**
   * Selection logic for multi-select (shift+click) moved from Redux thunk `selectValueRange`.
   *
   * Calculates the range of values from currently selected values to the clicked value
   * and selects all values in that rectangular range.
   */
  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      const startTime = performance.now();
      const reliability = ReliabilityMetrics.getInstance();

      try {
        closeMenu();

        const reader = getService('SelectionsReader');
        selectionsActions.clearSelectionIntent();
        if (options?.isMultiSelectOn) {
          const database = getDatabaseWithCurrentView(store.getState(), { contentId });

          if (!database) {
            selectionsActions.selectValues([{ entryId, fieldId }]);
            return;
          }

          const currentSelectedEntryIds = reader.getEntryIdsFromSelection();
          const currentSelectedFieldIds = reader.getFieldIdsFromSelection();

          const values = calculate2DValueRange(
            database.entries,
            database.fields,
            currentSelectedEntryIds,
            currentSelectedFieldIds,
            entryId,
            fieldId
          );

          selectionsActions.selectValues(values);
        } else {
          const tableRoot = document.querySelector<HTMLElement>(
            '[data-testid="layout-table"]'
          );

          selectionsActions.setFirstSelectedValue({ entryId, fieldId });
          selectionsActions.selectValues([{ entryId, fieldId }]);
          selectionsActions.setSelectionIntent({ type: 'softSelection' });

          fireCellSelectionMetric({
            startTime,
            probeData: {
              tableRoot,
              selectionProbe: 'legacyTdIsSelected',
              entryId,
              fieldId,
            },
            metadata: {
              contentId,
              selectionProbe: 'legacyTdIsSelected',
            },
            ...(!tableRoot && {
              failImmediately: { reason: 'cell-selection-dom-sync-missing-table-root' },
            }),
          });
        }
        reliability.success(MetricName.DATABASE_SELECTED);
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        reliability.failure(MetricName.DATABASE_SELECTED, {
          errorMessage,
        });
        throw e;
      }
    },
    [
      closeMenu,
      contentId,
      selectionsActions,
      entryId,
      fieldId,
      store,
      fireCellSelectionMetric,
    ]
  );

  const handleSelectValue = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      const reader = getService('SelectionsReader');
      const multipleValuesSelected = reader.getSelectionCount() > 1;
      const isMultiSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
      if (!multipleValuesSelected) {
        onIsSelectedChange({ isMultiSelectOn });
      }
    },
    [onIsSelectedChange]
  );

  const isContextMenuEnabled = isPluginEnabled(PluginKey.CONTEXT_MENU);

  const [menuItems, setMenuItems] = useState<MenuItemMap>({});
  const [canInsertContent, setCanInsertContent] = useState(false);
  const dialogs = useDialog();
  const onCloseMenu = () => {
    setMenuItems({});
  };

  const actionBar = useSnapshot(actionBarState.state);

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const startTime = performance.now();

      if (actionBar.isActionBarEnabled) {
        return;
      }

      handleSelectValue(e);

      // Show native context menu on text edit
      const target = e.target as HTMLElement;
      const isEditableTextArea = target.tagName.toLocaleLowerCase() === 'textarea';
      if (isEditableTextArea) {
        return;
      }

      const context = createContextualMenuContext({
        canEditContent,
        canEditFields,
        comments: {
          setOpenThread,
        },
        currentViewId,
        contentId,
        dialogs,
        entryId,
        fieldId,
        formatMessage,
        isDBAiFirstCreationEnabled,
      });
      const computedMenuItems = createContextualMenuItems(context);

      setMenuItems(computedMenuItems);
      setCanInsertContent(context.capabilities.canInsertContent);

      const isSelected = getService('SelectionsReader').isValueSelected(entryId, fieldId);

      if (openThread?.valueId === getValueId(entryId, fieldId)) {
        return;
      }

      if (isSelected && isContentEditable) {
        e.preventDefault();
        void analyticsClient.sendValueContextMenuOpenedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsAnalyticsAttributes(contentId),
        });

        fireCellContextMenuMetric({
          startTime,
          probeData: { entryId, fieldId },
          metadata: {
            contentId,
          },
        });

        toggleOpenMenu();
        blurActiveElement();
      }
    },
    [
      handleSelectValue,
      canEditContent,
      canEditFields,
      contentId,
      currentViewId,
      dialogs,
      entryId,
      fieldId,
      formatMessage,
      isDBAiFirstCreationEnabled,
      isContentEditable,
      openThread?.valueId,
      toggleOpenMenu,
      setOpenThread,
      actionBar.isActionBarEnabled,
      analyticsClient,
      fireCellContextMenuMetric,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps?: TriggerProps) => (
      <LegacyTableLayoutCell
        contentId={contentId}
        entryId={entryId}
        fieldId={fieldId}
        rowIndex={rowIndex}
        colIndex={colIndex}
        isContentEditable={isContentEditable}
        canEditContent={canEditContent}
        canEditFields={canEditFields}
        currentViewId={currentViewId}
        isSelectionDisabled={isSelectionDisabled}
        onIsSelectedChange={onIsSelectedChange}
        handleOnContextMenu={isContextMenuEnabled ? handleOnContextMenu : undefined}
        {...LegacyTableLayoutCellProps}
        {...triggerProps}
        isFirstInRow={LegacyTableLayoutCellProps.isFirstInRow}
        ref={triggerProps?.ref}
      />
    ),
    [
      canEditContent,
      canEditFields,
      colIndex,
      contentId,
      currentViewId,
      entryId,
      fieldId,
      handleOnContextMenu,
      isContentEditable,
      isSelectionDisabled,
      onIsSelectedChange,
      rowIndex,
      LegacyTableLayoutCellProps,
      isContextMenuEnabled,
    ]
  );

  return (
    <SelectValueMenu
      Trigger={triggerComponent}
      contentId={contentId}
      currentViewId={currentViewId}
      canEditContent={canEditContent}
      entryId={entryId}
      fieldId={fieldId}
      menuItems={menuItems}
      canInsertContent={canInsertContent}
      onCloseMenu={onCloseMenu}
    />
  );
});

export default TableLayoutCellWithMenu;
